/* eslint-disable no-nested-ternary */
import React, {useCallback, useEffect, useMemo} from 'react'
import styled from 'styled-components'
import useLanguage from 'features/languageReport/useLanguage'
import {Typography} from '@material-ui/core'
import mark from 'stories/assets/mark.svg'
import warning from 'stories/assets/warning.svg'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import {FiveFeaturesScore, Frequency} from 'api/healthCareApi'
import PercentChangeBarCard from 'pages/HealthCenter/ReportV1/HealthCenterMajorIndicators/Elements/percentChangeBar'
import NotificationCard from 'pages/HealthCenter/ReportV1/HealthCenterMajorIndicators/Elements/notificationCard'
import {Spacing} from 'components/common/useBaseStyle'
import useHealthCenterStyles from 'pages/HealthCenter/ReportV1/HealthCenterMajorIndicators/Style'
import {decodeHTMLEntities} from 'helpers/analysisHelper'
import {fetchFollowupTextContent} from 'api/analysisApi'
import {useTranslation} from 'react-i18next'
import {FiveMajorFeatures} from 'api/languageApi'

export const PageWrap = styled.div`
  background: white;
`
export const PageContent = styled.div`
  width: 1160px;
`

export const PageCard = styled.div`
  width: 100%;
  margin-bottom: 60px;
`
export const HeaderCard = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  border-bottom: 1px solid #00a29c;
`
export const HeaderCardNumber = styled.div`
  background-color: #2e4d6f;
  color: white;
  background-color: #2e4d6f;
  width: 42px;
  height: 42px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0px 10px;
`

export const HeaderCardTitle = styled.div`
  font-size: 20px;
  height: 42px;
  display: flex;
  align-items: center;
  padding-left: 15px;
`

export const HeaderCardTitleGreen = styled.div`
  font-size: 18px;
  font: 'Noto Sans KR';
  font-weight: 500;
  line-height: 18px;
  height: 40px;
  display: flex;
  align-items: center;
  border-radius: 10px 10px 0px 0px;
  background-color: #00a29c;
  color: white;
  padding: 10px 24px 12px;
`

export const CardContent = styled.div`
  font-size: 14px;
`

export const Line = styled.div`
  height: 1px;
  background-color: #2e4d6f;
  width: 1150px;
`

export const EmptyBox = styled.div`
  width: 10px;
`

export const ReportCustomPadding = styled.div`
  padding: ${Spacing.report.reportCustomPadding}px;
`

export const FlexBox = styled.div`
  display: flex;
  padding: 0 15px;
  justify-content: space-between;
`

export interface HealthCenterMajorProps {
  fiveFeaturesRadarImage: string
  fiveFeaturesScore: FiveFeaturesScore
  isEEG?: boolean
  scoreAnnotation: string
  footer: string
}

export type EmotionKey =
  | 'sadness'
  | 'tension'
  | 'stress'
  | 'inattention'
  | 'dominant_frequency'

function HealthCenterMajorPage({
  fiveFeaturesRadarImage,
  fiveFeaturesScore,
  isEEG = false,
  scoreAnnotation,
  footer,
}: HealthCenterMajorProps) {
  const classes = useHealthCenterStyles()
  const [textAPI, setTextAPI] = React.useState<FiveMajorFeatures | null>(null)

  const percentile = textAPI?.threshold.t_score || {
    low: 30,
    high: 70,
  }

  const {i18n} = useTranslation()

  const getIconAnalysis = useCallback(
    (percent: number, color?: boolean) => {
      const value = !Number.isNaN(Number(percent))
        ? Math.floor(Number(percent))
        : 0
      if (color && value < percentile.low) return warning
      if (value > percentile.high) return warning
      return mark
    },
    [percentile],
  )

  const getBackgroundColorAnalysis = useCallback(
    (percent: number, colorBlue?: boolean) => {
      const value = !Number.isNaN(Number(percent))
        ? Math.floor(Number(percent))
        : 0
      if (colorBlue && value < percentile.low) return '#fef0f0'
      if (value > percentile.high) return '#fef0f0'
      return 'white'
    },
    [percentile],
  )

  const getTextColor = useCallback(
    (percent: number, colorBlue?: boolean) => {
      const value = !Number.isNaN(Number(percent))
        ? Math.floor(Number(percent))
        : 0
      if (colorBlue && value < percentile.low) return 'red'
      if (value > percentile.high) return 'red'
      return '#000000'
    },
    [percentile],
  )

  const getTextAnalysis = useCallback(
    (
      percent: number,
      contentGood: string,
      contentNormal: string,
      contentBad: string,
    ) => {
      const value = !Number.isNaN(Number(percent))
        ? Math.floor(Number(percent))
        : 0
      if (value < percentile.low) return contentGood
      if (value > percentile.high) return contentBad
      return contentNormal
    },
    [percentile],
  )

  const dataNotification = useMemo(
    () => [
      {
        color: getTextColor(fiveFeaturesScore?.sadness),
        iconName: getIconAnalysis(fiveFeaturesScore?.sadness),
        headerName: textAPI?.section.chart.gauge_bars.sadness.title ?? '',
        content: getTextAnalysis(
          fiveFeaturesScore?.sadness,
          textAPI?.section.chart.analysis_results.opinions.sadness.low.main ??
            '',
          textAPI?.section.chart.analysis_results.opinions.sadness.normal
            .main ?? '',
          textAPI?.section.chart.analysis_results.opinions.sadness.high.main ??
            '',
        ),
        childContent: getTextAnalysis(
          fiveFeaturesScore?.sadness,
          textAPI?.section.chart.analysis_results.opinions.sadness.low.sub ??
            '',
          textAPI?.section.chart.analysis_results.opinions.sadness.normal.sub ??
            '',
          textAPI?.section.chart.analysis_results.opinions.sadness.high.sub ??
            '',
        ),
        backgroundColor: getBackgroundColorAnalysis(fiveFeaturesScore?.sadness),
      },
      {
        color: getTextColor(fiveFeaturesScore?.tension),
        iconName: getIconAnalysis(fiveFeaturesScore?.tension),
        headerName: textAPI?.section.chart.gauge_bars.tension.title ?? '',
        content: getTextAnalysis(
          fiveFeaturesScore?.tension,
          textAPI?.section.chart.analysis_results.opinions.tension.low.main ??
            '',
          textAPI?.section.chart.analysis_results.opinions.tension.normal
            .main ?? '',
          textAPI?.section.chart.analysis_results.opinions.tension.high.main ??
            '',
        ),
        childContent: getTextAnalysis(
          fiveFeaturesScore?.tension,
          textAPI?.section.chart.analysis_results.opinions.tension.low.sub ??
            '',
          textAPI?.section.chart.analysis_results.opinions.tension.normal.sub ??
            '',
          textAPI?.section.chart.analysis_results.opinions.tension.high.sub ??
            '',
        ),
        backgroundColor: getBackgroundColorAnalysis(fiveFeaturesScore?.tension),
      },
      {
        color: getTextColor(fiveFeaturesScore?.stress),
        iconName: getIconAnalysis(fiveFeaturesScore?.stress),
        headerName: textAPI?.section.chart.gauge_bars.stress.title ?? '',
        content: getTextAnalysis(
          fiveFeaturesScore?.stress,
          textAPI?.section.chart.analysis_results.opinions.stress.low.main ??
            '',
          textAPI?.section.chart.analysis_results.opinions.stress.normal.main ??
            '',
          textAPI?.section.chart.analysis_results.opinions.stress.high.main ??
            '',
        ),
        childContent: getTextAnalysis(
          fiveFeaturesScore?.stress,
          textAPI?.section.chart.analysis_results.opinions.stress.low.sub ?? '',
          textAPI?.section.chart.analysis_results.opinions.stress.normal.sub ??
            '',
          textAPI?.section.chart.analysis_results.opinions.stress.high.sub ??
            '',
        ),
        backgroundColor: getBackgroundColorAnalysis(fiveFeaturesScore?.stress),
      },
      {
        color: getTextColor(fiveFeaturesScore?.dominant_frequency?.score, true),
        iconName: getIconAnalysis(
          fiveFeaturesScore?.dominant_frequency?.score,
          true,
        ),
        headerName:
          textAPI?.section.chart.gauge_bars.dominant_frequency.title ?? '',
        content: getTextAnalysis(
          fiveFeaturesScore?.dominant_frequency?.score,
          textAPI?.section.chart.analysis_results.opinions.dominant_frequency
            .low.main ?? '',
          textAPI?.section.chart.analysis_results.opinions.dominant_frequency
            .normal.main ?? '',
          textAPI?.section.chart.analysis_results.opinions.dominant_frequency
            .high.main ?? '',
        ),
        childContent: getTextAnalysis(
          fiveFeaturesScore?.dominant_frequency?.score,
          textAPI?.section.chart.analysis_results.opinions.dominant_frequency
            .low.sub ?? '',
          textAPI?.section.chart.analysis_results.opinions.dominant_frequency
            .normal.sub ?? '',
          textAPI?.section.chart.analysis_results.opinions.dominant_frequency
            .high.sub ?? '',
        ),
        backgroundColor: getBackgroundColorAnalysis(
          fiveFeaturesScore?.dominant_frequency?.score,
          true,
        ),
      },
      {
        color: getTextColor(fiveFeaturesScore?.inattention?.score),
        iconName: getIconAnalysis(fiveFeaturesScore?.inattention?.score),
        headerName: textAPI?.section.chart.gauge_bars.inattention.title ?? '',
        content: getTextAnalysis(
          fiveFeaturesScore?.inattention?.score,
          textAPI?.section.chart.analysis_results.opinions.inattention.low
            .main ?? '',
          textAPI?.section.chart.analysis_results.opinions.inattention.normal
            .main ?? '',
          textAPI?.section.chart.analysis_results.opinions.inattention.high
            .main ?? '',
        ),
        childContent: getTextAnalysis(
          fiveFeaturesScore?.inattention?.score,
          textAPI?.section.chart.analysis_results.opinions.inattention.low
            .sub ?? '',
          textAPI?.section.chart.analysis_results.opinions.inattention.normal
            .sub ?? '',
          textAPI?.section.chart.analysis_results.opinions.inattention.high
            .sub ?? '',
        ),
        backgroundColor: getBackgroundColorAnalysis(
          fiveFeaturesScore?.inattention?.score,
        ),
      },
    ],
    [textAPI, fiveFeaturesScore],
  )

  const formatColorLeft = useCallback(
    (value: number | string) => {
      // if (Math.floor(Number(value)) < percentile.low) return '#1eaae3'
      if (Math.floor(Number(value)) > percentile.high) return '#ee3d3b'
      return '#B4B4B4'
    },
    [textAPI],
  )

  const formatColorRight = useCallback(
    (value: number | string) => {
      if (Math.floor(Number(value)) < percentile.low) return '#ee3d3b'
      if (Math.floor(Number(value)) > percentile.high) return '#ee3d3b'
      return '#B4B4B4'
    },
    [textAPI],
  )

  // Special text for typeStatus = 3 (Speed), typeStatus = 1(good and bad), typeStatus = 2 (normal and good)
  const getTextInRound = useCallback(
    (value: number, typeStatus: number, key: EmotionKey) => {
      if (!textAPI) return ''

      if (Math.floor(Number(value)) > percentile.high)
        return textAPI.section.chart.gauge_bars[key].status.high
      if (Math.floor(Number(value)) < percentile.low)
        return textAPI.section.chart.gauge_bars[key].status.low
      return textAPI.section.chart.gauge_bars[key].status.normal
    },
    [textAPI],
  )

  const getNumber = (value: number) => {
    if (!Number.isNaN(Number(value))) return value
    return 0
  }

  // typeStatus = 1 show text 좋음(X) 나쁨(O) and typeStatus != 1 show text 좋음(X) 정상(O)
  const formatTextRound = (
    frequency: Frequency,
    type: number,
    typeStatus: number,
    key: EmotionKey,
  ) => {
    if (type === 1)
      return {
        value: Math.floor(getNumber(frequency?.score)),
        frequency: `(${getNumber(frequency?.value)}Hz)`,
        status: getTextInRound(getNumber(frequency?.score), typeStatus, key),
      }
    return {
      value: Math.floor(getNumber(frequency?.score)),
      frequency: `(${getNumber(frequency?.value)})`,
      status: getTextInRound(getNumber(frequency?.score), typeStatus, key),
    }
  }

  const formatTextRoundLeft = (
    frequency: number,
    type: number,
    typeStatus: number,
    key: EmotionKey,
  ) => {
    return {
      value: Math.floor(getNumber(frequency)),
      status: String(getTextInRound(getNumber(frequency), typeStatus, key)),
    }
  }

  const getStaticContent = async () => {
    const res = await fetchFollowupTextContent({
      type: 'brain_top_indicators',
      lang: i18n.language,
    })
    setTextAPI(res.five_major_features)
  }

  useEffect(() => {
    getStaticContent()
  }, [i18n.language])

  return (
    <div className={classes.fiveMajorIndicators}>
      <PageWrap>
        <PageContent>
          <WrapElement number={1} text={textAPI?.section?.graph.title || ''}>
            <CardContent>
              <FlexBox>
                <div className={classes.imageChart}>
                  <img src={fiveFeaturesRadarImage} alt='RadarImage' />
                </div>
                <div className={classes.textChart}>
                  <div>
                    <div style={{width: 606}}>
                      <div className={classes.textWeight}>
                        <p className={classes.textThroughAI}>
                          {/* {t('IThroughAI')} */}
                          {textAPI?.section?.graph.description[0] || ''}
                        </p>
                        <p className={classes.textResultsOfPersonal}>
                          {/* {t('IResultsOfPersonal')} */}
                          {textAPI?.section?.graph.description[1] || ''}
                        </p>
                      </div>
                      <Typography className={classes.padding} />
                      <div>
                        <p className={classes.notificationChildContent}>
                          {/* {t('IComprehensively')} */}
                          {textAPI?.section?.graph.description[2] || ''}
                        </p>
                      </div>
                    </div>
                    <Typography className={classes.padding} />
                    <Typography className={classes.padding} />
                    <div className={classes.imageDescription}>
                      <Typography className={classes.padding} />
                      <ul className={classes.list}>
                        <li>
                          <p className={classes.textList}>
                            {/* {t('IDeficitBetter')} */}
                            {textAPI?.section?.graph.text_box[0] || ''}
                          </p>
                        </li>
                        <li>
                          <p className={classes.textList}>
                            {/* {t('IScoresIndicate')} */}
                            {textAPI?.section?.graph.text_box[1] || ''}
                          </p>
                        </li>
                        <li>
                          <p className={classes.textList}>
                            {/* {t('IFindingsAppear')} */}
                            {textAPI?.section?.graph.text_box[2] || ''}
                          </p>
                        </li>
                        <li>
                          <p className={classes.textList}>
                            {/* {t('IReMeasurement')} */}
                            {textAPI?.section?.graph.text_box[3] || ''}
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </FlexBox>
            </CardContent>
          </WrapElement>

          <WrapElement number={2} text={textAPI?.section?.chart.title || ''}>
            <CardContent>
              <FlexBox>
                <div className={classes.pullUpContent}>
                  <PercentChangeBarCard
                    headerName={
                      textAPI?.section?.chart.gauge_bars.sadness.title || ''
                    }
                    backgroundColor={formatColorLeft(
                      fiveFeaturesScore?.sadness,
                    )}
                    textRound={formatTextRoundLeft(
                      fiveFeaturesScore?.sadness,
                      2,
                      1,
                      'sadness',
                    )}
                    percentage={fiveFeaturesScore?.sadness ?? 0}
                    index={1}
                    top={
                      textAPI?.section?.chart.gauge_bars.sadness.status.low ||
                      ''
                    }
                    middle={
                      textAPI?.section?.chart.gauge_bars.sadness.status
                        .normal || ''
                    }
                    bottom={
                      textAPI?.section?.chart.gauge_bars.sadness.status.high ||
                      ''
                    }
                    isEEG={isEEG}
                  />

                  <PercentChangeBarCard
                    headerName={
                      textAPI?.section?.chart.gauge_bars.tension.title || ''
                    }
                    backgroundColor={formatColorLeft(
                      fiveFeaturesScore?.tension,
                    )}
                    textRound={formatTextRoundLeft(
                      fiveFeaturesScore?.tension,
                      2,
                      2,
                      'tension',
                    )}
                    percentage={fiveFeaturesScore?.tension ?? 0}
                    index={2}
                    top={
                      textAPI?.section?.chart.gauge_bars.tension.status.low ||
                      ''
                    }
                    middle={
                      textAPI?.section?.chart.gauge_bars.tension.status
                        .normal || ''
                    }
                    bottom={
                      textAPI?.section?.chart.gauge_bars.tension.status.high ||
                      ''
                    }
                    isEEG={isEEG}
                  />

                  <PercentChangeBarCard
                    headerName={
                      textAPI?.section?.chart.gauge_bars.stress.title || ''
                    }
                    backgroundColor={formatColorLeft(fiveFeaturesScore?.stress)}
                    textRound={formatTextRoundLeft(
                      fiveFeaturesScore?.stress,
                      2,
                      2,
                      'stress',
                    )}
                    percentage={fiveFeaturesScore?.stress ?? 0}
                    index={3}
                    top={
                      textAPI?.section?.chart.gauge_bars.stress.status.low || ''
                    }
                    middle={
                      textAPI?.section?.chart.gauge_bars.stress.status.normal ||
                      ''
                    }
                    bottom={
                      textAPI?.section?.chart.gauge_bars.stress.status.high ||
                      ''
                    }
                    isEEG={isEEG}
                  />
                </div>
                <div className={classes.paddingLeft40}>
                  <PercentChangeBarCard
                    headerName={
                      textAPI?.section?.chart.gauge_bars.dominant_frequency
                        .title || ''
                    }
                    backgroundColor={formatColorRight(
                      getNumber(fiveFeaturesScore?.dominant_frequency?.score)
                        ? fiveFeaturesScore?.dominant_frequency?.score
                        : 0,
                    )}
                    textRound={formatTextRound(
                      fiveFeaturesScore?.dominant_frequency,
                      1,
                      3,
                      'dominant_frequency',
                    )}
                    percentage={
                      getNumber(fiveFeaturesScore?.dominant_frequency?.score) ??
                      0
                    }
                    index={4}
                    top={
                      textAPI?.section?.chart.gauge_bars.dominant_frequency
                        .status.low || ''
                    }
                    middle={
                      textAPI?.section?.chart.gauge_bars.dominant_frequency
                        .status.normal || ''
                    }
                    bottom={
                      textAPI?.section?.chart.gauge_bars.dominant_frequency
                        .status.high || ''
                    }
                    isEEG={isEEG}
                  />

                  <PercentChangeBarCard
                    headerName={
                      textAPI?.section?.chart.gauge_bars.inattention.title || ''
                    }
                    backgroundColor={formatColorLeft(
                      getNumber(fiveFeaturesScore?.inattention?.score)
                        ? fiveFeaturesScore?.inattention?.score
                        : 0,
                    )}
                    textRound={formatTextRound(
                      fiveFeaturesScore?.inattention,
                      2,
                      2,
                      'inattention',
                    )}
                    percentage={
                      getNumber(fiveFeaturesScore?.inattention?.score) ??
                      getNumber(
                        fiveFeaturesScore?.inattention as unknown as number,
                      )
                    }
                    index={5}
                    top={
                      textAPI?.section?.chart.gauge_bars.inattention.status
                        .low || ''
                    }
                    middle={
                      textAPI?.section?.chart.gauge_bars.inattention.status
                        .normal || ''
                    }
                    bottom={
                      textAPI?.section?.chart.gauge_bars.inattention.status
                        .high || ''
                    }
                    isEEG={isEEG}
                  />
                </div>
              </FlexBox>
            </CardContent>
            <div className={classes.scoreAnnotation}>
              {decodeHTMLEntities(scoreAnnotation)}
            </div>
          </WrapElement>

          <PageCard>
            <HeaderCard>
              <HeaderCardTitleGreen>
                {textAPI?.section.chart.analysis_results.title ?? ''}
              </HeaderCardTitleGreen>
            </HeaderCard>

            {dataNotification.map((item, index) => (
              <NotificationCard
                key={index}
                color={item.color}
                iconName={item.iconName}
                headerName={item.headerName}
                content={item.content}
                childContent={item.childContent}
                backgroundColor={item.backgroundColor}
                index={index + 1}
              />
            ))}
          </PageCard>
          <div className={classes.footer}>{footer}</div>
        </PageContent>
      </PageWrap>
    </div>
  )
}

export default HealthCenterMajorPage
